import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage, getStatusLabel } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, 
  Users, 
  CurrencyDollar,
  MapPin,
  Airplane,
  CheckCircle,
  Clock,
  Crown,
  ArrowRight,
  Headset,
  Lightning,
  X,
  Link as LinkIcon,
  Copy,
  Check
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDashboardStats, getRecentPackages, getPackagesByStatus, subscribeCustomerService } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#0077FF', '#FFB300', '#0077FF', '#4CAF50', '#8BC34A'];

const KPICard = ({ title, value, subtitle, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="kpi-card"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{title}</p>
        <p className="text-4xl font-bold text-gray-900 stat-value" style={{ fontFamily: 'Bebas Neue' }}>
          {value}
        </p>
        {subtitle && (
          <p className="text-base text-gray-600 mt-1">{subtitle}</p>
        )}
      </div>
      <div className={`p-4 rounded-xl ${color}`}>
        <Icon size={28} weight="duotone" className="text-white" />
      </div>
    </div>
  </motion.div>
);

export default function DashboardPage() {
  const { t, language , tx } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { plan_name, plan, max_clients, max_users, current_clients, current_users, clientUsagePercent, userUsagePercent, handleUpgrade, customer_service_active } = usePlan();
  const [stats, setStats] = useState(null);
  const [recentPackages, setRecentPackages] = useState([]);
  const [packagesByStatus, setPackagesByStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [subscribingCS, setSubscribingCS] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSetupBanner, setShowSetupBanner] = useState(searchParams.get('setup') === 'true');
  const [readyToInvoice, setReadyToInvoice] = useState(null);

  const companyId = user?.company_id || '';
  // Always use the public production domain so the link shared with clients
  // is always the live trackmypackage.app site, regardless of which env
  // the tenant is logged in from (preview or production).
  const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://trackmypackage.app';
  const onboardingLink = companyId ? `${PUBLIC_SITE}/join/${companyId}` : '';

  const copyOnboardingLink = async () => {
    if (!onboardingLink) return;
    try {
      await navigator.clipboard.writeText(onboardingLink);
      setLinkCopied(true);
      toast.success(tx('Onboarding link copied!', 'Enlace de registro copiado!', 'Lien d\'inscription copié !'));
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, packagesRes, statusRes] = await Promise.all([
          getDashboardStats(),
          getRecentPackages(),
          getPackagesByStatus()
        ]);
        setStats(statsRes.data);
        setRecentPackages(packagesRes.data);
        setPackagesByStatus(statusRes.data.filter(s => s.count > 0));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Surface arrived-in-SJ packages awaiting invoicing
    api.get('/accounting/summary').then((res) => {
      const p = res.data?.pending || {};
      setReadyToInvoice({ count: p.count || 0, total_usd: p.total_usd || 0, total_kg: p.total_kg || 0 });
    }).catch(() => {});
  }, []);

  const handleSubscribeCS = async () => {
    setSubscribingCS(true);
    try {
      const res = await subscribeCustomerService();
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start subscription';
      alert(msg);
    } finally {
      setSubscribingCS(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    return `status-${status}`;
  };

  if (loading) {
    return (
      <Layout title={t('dashboard')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('dashboard')}>
      <div className="space-y-8 premium-fade-up" data-testid="dashboard-page">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            title={t('totalPackages')}
            value={stats?.packages?.total || 0}
            subtitle={`${stats?.packages?.in_transit || 0} ${tx('in transit', 'en tránsito', 'in transit')}`}
            icon={Package}
            color="bg-[#0077FF]"
          />
          <KPICard
            title={t('totalCustomers')}
            value={stats?.customers?.total || 0}
            icon={Users}
            color="bg-[#FFB300]"
          />
          <KPICard
            title={t('totalRevenue')}
            value={`$${(stats?.revenue?.total_usd || 0).toLocaleString()}`}
            subtitle={`₡${(stats?.revenue?.total_crc || 0).toLocaleString()}`}
            icon={CurrencyDollar}
            color="bg-[#0077FF]"
          />
          <KPICard
            title={t('pendingRevenue')}
            value={`$${(stats?.revenue?.pending_usd || 0).toLocaleString()}`}
            icon={Clock}
            color="bg-[#FF9800]"
          />
        </div>

        {/* Ready to Invoice — arrived in SJ with weight saved but not yet invoiced */}
        {readyToInvoice && readyToInvoice.count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
            data-testid="ready-to-invoice-banner"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Package size={28} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">
                {tx('Ready to Invoice', 'Listo para Facturar', 'Prêt à Facturer')}
              </p>
              <p className="text-lg sm:text-xl font-bold text-gray-900 mt-0.5">
                <span data-testid="ready-to-invoice-count">{readyToInvoice.count}</span> {readyToInvoice.count === 1 ? tx('package', 'paquete', 'colis') : tx('packages', 'paquetes', 'colis')}{' '}
                <span className="text-amber-700">· {readyToInvoice.total_kg.toFixed(1)} kg · ${readyToInvoice.total_usd.toFixed(2)}</span>
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {tx(
                  'Arrived in San José with weight saved — not yet invoiced',
                  'Llegaron a San José con peso guardado — aún no facturados',
                  'Arrivés à San José avec poids enregistré — pas encore facturés'
                )}
              </p>
            </div>
            <button
              onClick={() => navigate('/accounting')}
              className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm inline-flex items-center gap-2 transition-colors shadow-sm"
              data-testid="ready-to-invoice-cta"
            >
              {tx('Review & Invoice', 'Revisar y Facturar', 'Réviser & Facturer')}
              <ArrowRight size={16} weight="bold" />
            </button>
          </motion.div>
        )}

        {/* Welcome Setup Banner (new signups) */}
        <AnimatePresence>
          {showSetupBanner && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="relative bg-gradient-to-r from-[#003166] to-[#0050A0] rounded-xl p-6 border border-[#FF9900]/30"
              data-testid="setup-welcome-banner"
            >
              <button onClick={() => setShowSetupBanner(false)} className="absolute top-3 right-3 text-white/40 hover:text-white"><X size={16} /></button>
              <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }}>
                {tx('Welcome to Track My Package!', 'Bienvenido a Track My Package!', 'Bienvenue sur Track My Package !')}
              </h2>
              <p className="text-white/70 text-sm mb-4">{tx('Here\'s how to get started:', 'Aquí te mostramos cómo empezar:', 'Voici comment démarrer :')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button onClick={() => navigate('/customers')} className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-lg p-3 transition-colors text-left" data-testid="setup-step-1">
                  <div className="w-8 h-8 bg-[#FF9900] rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0">1</div>
                  <div>
                    <p className="text-white text-sm font-semibold">{tx('Add Your First Client', 'Agrega Tu Primer Cliente', 'Ajoutez Votre Premier Client')}</p>
                    <p className="text-white/50 text-xs">{tx('Create a client profile', 'Crea un perfil de cliente', 'Créez un profil client')}</p>
                  </div>
                </button>
                <button onClick={copyOnboardingLink} className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-lg p-3 transition-colors text-left" data-testid="setup-step-2">
                  <div className="w-8 h-8 bg-[#FF9900] rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0">2</div>
                  <div>
                    <p className="text-white text-sm font-semibold">{tx('Share Onboarding Link', 'Compartir Enlace', 'Partager le Lien')}</p>
                    <p className="text-white/50 text-xs">{tx('Let clients sign up themselves', 'Los clientes se registran solos', 'Les clients s\'inscrivent eux-mêmes')}</p>
                  </div>
                </button>
                <button onClick={() => navigate('/packages')} className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-lg p-3 transition-colors text-left" data-testid="setup-step-3">
                  <div className="w-8 h-8 bg-[#FF9900] rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0">3</div>
                  <div>
                    <p className="text-white text-sm font-semibold">{tx('Manage Packages', 'Gestionar Paquetes', 'Gérer les Colis')}</p>
                    <p className="text-white/50 text-xs">{tx('Track, scan & invoice', 'Rastrear, escanear y facturar', 'Suivre, scanner et facturer')}</p>
                  </div>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Client Onboarding Link Card */}
        {companyId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 shadow-sm rounded-xl p-5 flex items-center gap-4"
            data-testid="onboarding-link-card"
          >
            <div className="p-2.5 bg-[#003166] rounded-xl flex-shrink-0">
              <LinkIcon size={22} weight="duotone" className="text-[#FF9900]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">{tx('Client Onboarding Link', 'Enlace de Registro de Clientes', 'Lien d\'Inscription Client')}</p>
              <p className="text-xs text-gray-500 truncate" data-testid="onboarding-link-url">{onboardingLink}</p>
            </div>
            <button
              onClick={copyOnboardingLink}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                linkCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-[#FF9900] hover:bg-[#e08800] text-white'
              }`}
              data-testid="copy-onboarding-link-btn"
            >
              {linkCopied ? <><Check size={16} weight="bold" /> {tx('Copied!', 'Copiado!', 'Copié !')}</> : <><Copy size={16} /> {tx('Copy Link', 'Copiar Enlace', 'Copier le Lien')}</>}
            </button>
          </motion.div>
        )}

        {/* Plan Usage Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 shadow-sm rounded-xl p-6"
          data-testid="plan-usage-widget"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Crown size={22} weight="fill" className="text-[#FF9900]" />
              <h3 className="font-bold text-base text-gray-900 uppercase tracking-wide" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.08em' }}>
                {plan_name} Plan
              </h3>
            </div>
            {plan !== 'enterprise' && (
              <button
                onClick={handleUpgrade}
                className="text-sm text-[#FF9900] hover:text-[#003166] font-semibold flex items-center gap-1"
                data-testid="dashboard-upgrade-btn"
              >
                Upgrade <ArrowRight size={14} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="flex justify-between text-sm text-gray-500 mb-1.5">
                <span>{tx('Clients', 'Clientes', 'Clients')}</span>
                <span className="font-medium text-gray-700">{current_clients}/{max_clients === -1 ? '∞' : max_clients}</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${clientUsagePercent >= 90 ? 'bg-red-400' : clientUsagePercent >= 70 ? 'bg-yellow-400' : 'bg-[#0077FF]'}`}
                  style={{ width: `${Math.min(clientUsagePercent, 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm text-gray-500 mb-1.5">
                <span>{tx('Team Members', 'Usuarios', 'Team Members')}</span>
                <span className="font-medium text-gray-700">{current_users}/{max_users === -1 ? '∞' : max_users}</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${userUsagePercent >= 90 ? 'bg-red-400' : userUsagePercent >= 70 ? 'bg-yellow-400' : 'bg-[#0077FF]'}`}
                  style={{ width: `${Math.min(userUsagePercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* 24/7 Customer Service Promotion */}
        <AnimatePresence>
          {!customer_service_active && !promoDismissed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10, transition: { duration: 0.2 } }}
              className="relative overflow-hidden rounded-xl border border-[#FF9900]/30"
              data-testid="customer-service-promo"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#003166] via-[#003166] to-[#00245a]" />
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF9900]/10 rounded-full -translate-y-1/2 translate-x-1/4" />
              <div className="relative p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <button
                  onClick={() => setPromoDismissed(true)}
                  className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors"
                  data-testid="dismiss-promo-btn"
                >
                  <X size={18} />
                </button>
                <div className="flex-shrink-0 p-3.5 bg-[#FF9900] rounded-xl">
                  <Headset size={32} weight="duotone" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Lightning size={16} weight="fill" className="text-[#FF9900]" />
                    <span className="text-xs font-bold text-[#FF9900] uppercase tracking-wider">Add-on Available</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }}>
                    24/7 Courier Customer Service
                  </h3>
                  <p className="text-sm text-white/70 leading-relaxed">
                    Let our professional agents handle all your customer inquiries, tracking updates, and complaint resolution around the clock.
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-col items-center gap-2">
                  <div className="text-center">
                    <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>$99</span>
                    <span className="text-sm text-white/60">/mo</span>
                  </div>
                  <button
                    onClick={handleSubscribeCS}
                    disabled={subscribingCS}
                    className="px-6 py-2.5 bg-[#FF9900] hover:bg-[#FF9900]/90 text-white font-bold text-sm rounded-lg transition-all whitespace-nowrap disabled:opacity-60"
                    data-testid="subscribe-customer-service-btn"
                  >
                    {subscribingCS ? 'Loading...' : 'Subscribe Now'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Customer Service Badge */}
        {customer_service_active && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3"
            data-testid="customer-service-active-badge"
          >
            <Headset size={22} weight="duotone" className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">24/7 Courier Customer Service — Active</span>
            <CheckCircle size={18} weight="fill" className="text-emerald-500" />
          </motion.div>
        )}

        {/* Tracking Workflow Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[
            { key: 'intake_miami', icon: MapPin, color: 'bg-blue-500' },
            { key: 'in_transit', icon: Airplane, color: 'bg-amber-500' },
            { key: 'arrived_san_jose', icon: MapPin, color: 'bg-teal-500' },
            { key: 'ready_for_pickup', icon: Package, color: 'bg-green-500' },
            { key: 'delivered', icon: CheckCircle, color: 'bg-emerald-500' },
          ].map((item) => {
            const Icon = item.icon;
            const count = stats?.packages?.[item.key] || 0;
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="kpi-card flex items-center gap-4 p-5"
              >
                <div className={`p-3 ${item.color} rounded-lg`}>
                  <Icon size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>{count}</p>
                  <p className="text-sm text-gray-500 truncate">{getStatusLabel(item.key, language)}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Package Status Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-8 kpi-card"
          >
            <h3 
              className="text-xl font-bold tracking-wide text-gray-900 mb-5"
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {t('packageStatus')}
            </h3>
            <div className="h-64">
              {packagesByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={packagesByStatus}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="status" 
                      tick={{ fontSize: 10, fill: '#64748b' }}
                      tickFormatter={(status) => getStatusLabel(status, language)}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip 
                      formatter={(value) => [value, tx('Packages', 'Paquetes', 'colis')]}
                      labelFormatter={(status) => getStatusLabel(status, language)}
                    />
                    <Bar dataKey="count" fill="#0077FF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {tx('No package data available', 'Sin datos de paquetes', 'No package data available')}
                </div>
              )}
            </div>
          </motion.div>

          {/* Status Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-4 kpi-card"
          >
            <h3 
              className="text-xl font-bold tracking-wide text-gray-900 mb-5"
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {tx('Distribution', 'Distribución', 'Distribution')}
            </h3>
            <div className="h-64">
              {packagesByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={packagesByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="count"
                      nameKey="status"
                    >
                      {packagesByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, getStatusLabel(name, language)]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {tx('No data', 'Sin datos', 'No data')}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Recent Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="kpi-card"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 
              className="text-xl font-bold tracking-wide text-gray-900"
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {t('recentPackages')}
            </h3>
            <a 
              href="/packages" 
              className="text-base text-[#0077FF] font-medium hover:underline"
            >
              {t('viewAll')}
            </a>
          </div>
          
          {recentPackages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">{t('trackingNumber')}</th>
                    <th className="text-left py-3 px-4">{t('customer')}</th>
                    <th className="text-left py-3 px-4">{t('weight')}</th>
                    <th className="text-left py-3 px-4">{t('status')}</th>
                    <th className="text-right py-3 px-4">{t('cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPackages.map((pkg) => (
                    <tr key={pkg.id} className="border-b border-gray-100">
                      <td className="py-4 px-4 font-mono text-base">{pkg.tracking_number}</td>
                      <td className="py-4 px-4 text-base">{pkg.customer_name}</td>
                      <td className="py-4 px-4 text-base">{pkg.weight_lbs} lbs / {pkg.weight_kg} kg</td>
                      <td className="py-4 px-4">
                        <span className={`status-badge ${getStatusBadgeClass(pkg.status)}`}>
                          {getStatusLabel(pkg.status, language)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right font-medium text-base">${pkg.shipping_cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noPackages')}</p>
            </div>
          )}
        </motion.div>

        {/* Pricing Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="kpi-card bg-gradient-to-r from-[#0077FF] to-[#0077FF] text-white"
        >
          <h3 className="text-xl font-bold tracking-wide mb-3" style={{ fontFamily: 'Bebas Neue' }}>
            {tx('Current Rate', 'Tarifa Actual', 'Current Rate')}
          </h3>
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-white/70 text-base">{tx('Price per kilo', 'Precio por kilo', 'Prix par Kilo')}</p>
              <p className="text-3xl font-bold">${stats?.pricing?.per_kg || 14.00} USD</p>
              <p className="text-base text-white/80 mt-0.5">
                ≈ ₡{((stats?.pricing?.per_kg || 14) * (stats?.pricing?.exchange_rate || 452.07)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CRC
              </p>
              <p className="text-[11px] text-white/60 mt-0.5">{tx('Rounded up to next 0.5 kg', 'Redondeado a 0.5 kg', 'Arrondi à 0,5 kg')}</p>
            </div>
            <div>
              <p className="text-white/70 text-base">{tx('Small package flat fee', 'Paquetes pequeños (tarifa fija)', 'Forfait petit colis')}</p>
              <p className="text-3xl font-bold">${(stats?.pricing?.small_package_flat_fee ?? 10).toFixed(2)} USD</p>
              <p className="text-[11px] text-white/60 mt-0.5">
                {tx(
                  `Packages ≤ ${stats?.pricing?.small_package_threshold_kg ?? 0.5} kg`,
                  `Paquetes ≤ ${stats?.pricing?.small_package_threshold_kg ?? 0.5} kg`,
                  `Colis ≤ ${stats?.pricing?.small_package_threshold_kg ?? 0.5} kg`
                )}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-base">{tx('Exchange rate', 'Tipo de cambio', 'Taux de Change')}</p>
              <p className="text-3xl font-bold">₡{stats?.pricing?.exchange_rate || 452.07}</p>
              <p className="text-[11px] text-white/60 mt-0.5">{tx('per USD', 'por USD', 'par USD')}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
